import {useCallback, useMemo, useState} from 'preact/hooks';

import {Theme} from 'utilities/color';
import {JSXNode} from 'utilities/props';

import {LayoutProps, UiLineType} from '../../types';
import {
  LayoutContext,
  LayoutContextInterface,
  UILineWrapper,
} from '../../context';

import styles from './ShopPromiseLayoutInner.css';

export function ShopPromiseLayoutInner({
  isLoading,
  isHidden,
  hasBrand,
  theme,
  fontOverride,
  children,
}: LayoutProps) {
  const classes = useMemo(
    () =>
      new Map([
        [UiLineType.Title, styles.titleLine],
        [
          UiLineType.TitleWithBrand,
          [styles.titleLine, styles.Branded].join(' '),
        ],
        [UiLineType.BrandImage, styles.brand],
        [UiLineType.Destination, styles.destinationLine],
        [
          UiLineType.DestinationFullWidth,
          [styles.destinationLine, styles.fullWidth].join(' '),
        ],
        [UiLineType.ConditionallyFree, styles.conditionallyFreeLine],
        [UiLineType.MarketingMessage, styles.marketingMessageLine],
        [UiLineType.ExpiryLine, styles.expiryLine],
        [UiLineType.BeforeWeekend, styles.beforeWeekendLine],
        [UiLineType.AdditionalInfo, styles.additionalInfoLine],
        [UiLineType.FullSize, styles.fullSize],
        [UiLineType.ErrorLine, styles.errorLine],
        [UiLineType.MergedColumns, styles.mergedColumns],
      ]),
    [],
  );

  const wrapElement: UILineWrapper = useCallback(
    (type, children) =>
      children ? (
        <div className={classes.get(type) ?? ''} data-testid={type.toString()}>
          {children}
        </div>
      ) : null,
    [classes],
  );
  const [contentSlot, setContentSlot] = useState<JSXNode>(null);
  const [brandSlot, setBrandSlot] = useState<JSXNode>(null);
  const [genericSlot, setGenericSlot] = useState<JSXNode>(null);

  const layoutContext = useMemo(
    () =>
      ({
        lineWrapper: wrapElement,
        onSlotAdded: (name, slotContent) => {
          switch (name) {
            case 'Content':
              return setContentSlot(slotContent);
            case 'Brand':
              return setBrandSlot(slotContent);
            case 'Generic':
              return setGenericSlot(slotContent);
          }
        },
      } as LayoutContextInterface),
    [wrapElement],
  );

  const content: JSXNode = (
    <>
      <div className={styles.content}>{contentSlot}</div>
      <div className={styles.branding}>{brandSlot}</div>
      {genericSlot}
      {children}
    </>
  );

  const markup: JSXNode = (
    <div
      className={[
        styles.container,
        theme === Theme.Dark ? styles.Dark : '',
        isHidden ? styles.Hidden : '',
        fontOverride ? styles['--font-override'] : '',
      ].join(' ')}
    >
      <div
        className={[
          styles.details,
          hasBrand ? styles.IsBranded : null,
          isLoading ? styles.IsLoading : '',
        ].join(' ')}
      >
        {content}
      </div>
    </div>
  ) as unknown as JSXNode;

  return (
    <LayoutContext.Provider value={layoutContext}>
      {markup}
    </LayoutContext.Provider>
  );
}
